import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Upload, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getCategories, createCategory, updateCategory, deleteCategory, getSubcategories, createSubcategory, updateSubcategory, deleteSubcategory, uploadSubcategoryIcon, uploadFile } from '../../services/api';
import { toast } from 'sonner';

const CategoriesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', order: 0, is_active: true });
  const [catImageFile, setCatImageFile] = useState(null);
  const [catCatalogFile, setCatCatalogFile] = useState(null);

  // Subcategory state
  const [expandedCat, setExpandedCat] = useState(null);
  const [subcategories, setSubcategories] = useState({});
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({ name: '', slug: '', description: '', category_id: '', order: 0, is_active: true });
  const [iconFile, setIconFile] = useState(null);

  const fetchData = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchSubcategories = async (categoryId) => {
    try {
      const res = await getSubcategories(categoryId);
      setSubcategories(prev => ({ ...prev, [categoryId]: res.data }));
    } catch { toast.error('Failed to load subcategories'); }
  };

  const toggleExpand = (catId) => {
    if (expandedCat === catId) {
      setExpandedCat(null);
    } else {
      setExpandedCat(catId);
      if (!subcategories[catId]) fetchSubcategories(catId);
    }
  };

  // Category CRUD
  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', slug: '', description: '', order: 0, is_active: true });
    setCatImageFile(null);
    setCatCatalogFile(null);
    setDialogOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, slug: cat.slug, description: cat.description || '', order: cat.order || 0, is_active: cat.is_active !== false, image: cat.image || '', catalog_pdf: cat.catalog_pdf || '' });
    setCatImageFile(null);
    setCatCatalogFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = form.slug || form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      let imageUrl = form.image || null;
      let catalogUrl = form.catalog_pdf || null;
      if (catImageFile) {
        const res = await uploadFile(catImageFile, 'category');
        imageUrl = res.data.url;
      }
      if (catCatalogFile) {
        const res = await uploadFile(catCatalogFile, 'catalog');
        catalogUrl = res.data.url;
      }
      const payload = { ...form, slug, image: imageUrl, catalog_pdf: catalogUrl };
      if (editing) {
        await updateCategory(editing.id, payload);
        toast.success('Category updated');
      } else {
        await createCategory(payload);
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await deleteCategory(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  // Subcategory CRUD
  const openSubCreate = (categoryId) => {
    setEditingSub(null);
    setSubForm({ name: '', slug: '', description: '', category_id: categoryId, order: 0, is_active: true });
    setIconFile(null);
    setSubDialogOpen(true);
  };

  const openSubEdit = (sub) => {
    setEditingSub(sub);
    setSubForm({
      name: sub.name, slug: sub.slug, description: sub.description || '',
      category_id: sub.category_id, order: sub.order || 0, is_active: sub.is_active !== false
    });
    setIconFile(null);
    setSubDialogOpen(true);
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    const slug = subForm.slug || subForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      let iconUrl = editingSub?.icon_url || null;
      if (iconFile) {
        const uploadRes = await uploadSubcategoryIcon(iconFile);
        iconUrl = uploadRes.data.url;
      }
      const payload = { ...subForm, slug, icon_url: iconUrl };
      if (editingSub) {
        await updateSubcategory(editingSub.id, payload);
        toast.success('Subcategory updated');
      } else {
        await createSubcategory(payload);
        toast.success('Subcategory created');
      }
      setSubDialogOpen(false);
      fetchSubcategories(subForm.category_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSubDelete = async (sub) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try {
      await deleteSubcategory(sub.id);
      toast.success('Deleted');
      fetchSubcategories(sub.category_id);
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="categories-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categories</h1>
          <p className="text-sm text-gray-500">{categories.length} categories</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-category-btn">
          <Plus size={16} className="mr-2" /> Add Category
        </Button>
      </div>

      {/* Image Size Guide */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 text-sm text-amber-800">
          <h4 className="font-semibold mb-2">Recommended Image Sizes</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <span><strong>Category Image:</strong> 800 x 600 px</span>
            <span><strong>Subcategory Icon:</strong> 64 x 64 px (PNG, transparent bg)</span>
            <span><strong>Product Image:</strong> 600 x 400 px</span>
            <span><strong>Banner Image:</strong> 1920 x 600 px</span>
            <span><strong>Client Logo:</strong> 200 x 100 px (PNG, transparent bg)</span>
            <span><strong>Event Photo:</strong> 800 x 600 px</span>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-16" /></Card>)}</div>
      ) : categories.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-12 text-center text-gray-500">No categories yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {categories.map((cat) => (
            <div key={cat.id}>
              <Card className="hover:shadow-sm transition-shadow" data-testid={`category-${cat.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => toggleExpand(cat.id)}>
                      {expandedCat === cat.id ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                          {!cat.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button size="sm" variant="outline" onClick={() => openSubCreate(cat.id)} className="text-xs">
                        <Plus size={12} className="mr-1" /> Sub
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(cat)}><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(cat.id)} className="text-red-600 hover:bg-red-50"><Trash2 size={14} /></Button>
                    </div>
                  </div>

                  {/* Expanded Subcategories */}
                  {expandedCat === cat.id && (
                    <div className="mt-4 ml-7 space-y-2 border-l-2 border-[#2E3A6F]/10 pl-4">
                      {!subcategories[cat.id] ? (
                        <p className="text-xs text-gray-400">Loading...</p>
                      ) : subcategories[cat.id].length === 0 ? (
                        <p className="text-xs text-gray-400">No subcategories. Click "+ Sub" to add one.</p>
                      ) : (
                        subcategories[cat.id].map((sub) => (
                          <div key={sub.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`subcategory-${sub.id}`}>
                            <div className="flex items-center gap-3">
                              {sub.icon_url ? (
                                <img
                                  src={sub.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${sub.icon_url}` : sub.icon_url}
                                  alt={sub.name}
                                  className="w-8 h-8 object-contain rounded"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded bg-[#2E3A6F]/10 flex items-center justify-center">
                                  <div className="w-4 h-4 rounded-full bg-[#2E3A6F]/30" />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{sub.name}</p>
                                <p className="text-[10px] text-gray-400">Slug: {sub.slug} &middot; Products: {sub.product_count || 0}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => openSubEdit(sub)} className="h-7 w-7 p-0"><Pencil size={12} /></Button>
                              <Button size="sm" variant="ghost" onClick={() => handleSubDelete(sub)} className="h-7 w-7 p-0 text-red-500"><Trash2 size={12} /></Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Category Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Category</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="category-name-input" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div>
              <Label>Category Image (800x600 px)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setCatImageFile(e.target.files[0])} className="text-sm" data-testid="category-image-upload" />
              {editing?.image && !catImageFile && <p className="text-[10px] text-gray-400 mt-1">Current: {editing.image}</p>}
            </div>
            <div>
              <Label>Category Catalog PDF</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setCatCatalogFile(e.target.files[0])} className="text-sm" data-testid="category-catalog-upload" />
              {editing?.catalog_pdf && !catCatalogFile && <p className="text-[10px] text-gray-400 mt-1">Current: {editing.catalog_pdf}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="category-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subcategory Dialog */}
      <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingSub ? 'Edit' : 'Add'} Subcategory</DialogTitle></DialogHeader>
          <form onSubmit={handleSubSubmit} className="space-y-4">
            <div><Label>Name *</Label><Input value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} required data-testid="subcategory-name-input" /></div>
            <div><Label>Slug</Label><Input value={subForm.slug} onChange={(e) => setSubForm({ ...subForm, slug: e.target.value })} placeholder="auto-generated" /></div>
            <div><Label>Description</Label><Textarea value={subForm.description} onChange={(e) => setSubForm({ ...subForm, description: e.target.value })} rows={2} /></div>

            {/* Custom Icon Upload */}
            <div>
              <Label>Process Icon (64x64 px, PNG recommended)</Label>
              <div className="flex items-center gap-3 mt-1">
                {(editingSub?.icon_url || iconFile) && (
                  <div className="w-12 h-12 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden">
                    {iconFile ? (
                      <img src={URL.createObjectURL(iconFile)} alt="Preview" className="w-10 h-10 object-contain" />
                    ) : editingSub?.icon_url ? (
                      <img
                        src={editingSub.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${editingSub.icon_url}` : editingSub.icon_url}
                        alt="Current"
                        className="w-10 h-10 object-contain"
                      />
                    ) : null}
                  </div>
                )}
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 hover:border-[#2E3A6F] transition-colors text-sm text-gray-600">
                    <Upload size={14} />
                    {iconFile ? iconFile.name : 'Upload custom icon'}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setIconFile(e.target.files[0])} data-testid="subcategory-icon-upload" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order</Label><Input type="number" value={subForm.order} onChange={(e) => setSubForm({ ...subForm, order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={subForm.is_active} onCheckedChange={(v) => setSubForm({ ...subForm, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="subcategory-submit-btn">{editingSub ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setSubDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesAdmin;
